// "不知道说什么？" — starter sentences for the talk box. Each idea is one moment
// of a match; we only offer the ones this car's rules don't cover yet, so the
// list doubles as a checklist of situations the kid hasn't thought about.
import { FIRE_ACTIONS, MOVE_ACTIONS, SENSORS, type Rule } from '../shared/defs';
import { h } from './api';

interface Idea { moment: string; say: string; done: (rules: Rule[]) => boolean }
const has = (rules: Rule[], cond: string) => rules.some((r) => r.when.includes(cond));
const does = (rules: Rule[], p: (a: string) => boolean) => rules.some((r) => p(r.do));

// Sentences come from tests/translator-cases.json, so both the LLM and the
// offline translator are known to understand them.
const IDEAS: Idea[] = [
  { moment: '开局去哪', say: '去偷对方的旗', done: (r) => does(r, (a) => a === 'go_to:enemy_flag') },
  { moment: '拿到旗以后', say: '拿到旗就回家', done: (r) => has(r, 'carrying_flag=yes') },
  { moment: '碰到敌人', say: '敌人很近的时候向它开火', done: (r) => does(r, (a) => a.startsWith('fire:')) },
  { moment: '自家旗被偷', say: '自家旗被偷了就去追拿旗的人', done: (r) => has(r, 'my_flag=taken') || does(r, (a) => a === 'chase:flag_carrier') },
  { moment: '没子弹了', say: '没子弹了就回家', done: (r) => has(r, 'my_ammo=empty') },
  { moment: '血少了', say: '血少就跑', done: (r) => has(r, 'my_hp=low') },
  { moment: '队友拿到旗', say: '队友拿到旗就跟着保护', done: (r) => has(r, 'enemy_flag=taken') || does(r, (a) => a === 'follow:teammate_with_flag') },
  { moment: '走哪条路', say: '走上路去偷旗', done: (r) => r.some((x) => x.lane && x.lane !== 'any') },
  { moment: '敌人太多', say: '看到好多敌人就躲开', done: (r) => has(r, 'enemies_seen=many') },
  { moment: '能量点', say: '去占能量点', done: (r) => does(r, (a) => a.endsWith(':energy_point')) },
];
const SHOW = 3;

export function talkIdeas(car: number, rules: Rule[], pick: (say: string) => void) {
  const todo = IDEAS.filter((i) => !i.done(rules));
  const box = h('div', { class: 'ideas' });
  box.append(h('div', { class: 'ideas-head' }, '💡 不知道说什么？想想比赛里会发生什么，点一句放进输入框，可以改了再发送：'));
  if (!rules.length) box.append(h('p', { class: 'ideas-empty' }, `${car + 1}号车现在一条规则都没有，开局会站着不动。第一句先告诉它：开局去哪儿？`));
  if (todo.length) {
    box.append(h('div', { class: 'row wrap' }, ...todo.slice(0, SHOW).map((i, k) =>
      h('button', { class: `chip idea ${!rules.length && k === 0 ? 'first' : ''}`, onclick: () => pick(i.say) },
        h('span', { class: 'moment' }, i.moment), ` ${i.say}`))));
  } else {
    box.append(h('p', { class: 'muted small' }, '常见的情况都说过了。看回放，找它犯傻的那一刻，再补一句。'));
  }
  box.append(h('p', { class: 'muted small' }, '说法很简单：', h('b', {}, '如果……就……'), '。比如“如果没子弹了，就回家”。'));
  const sees = Object.values(SENSORS).map((s) => `${s.label}：${Object.values(s.values).join(' / ')}`);
  box.append(h('details', { class: 'ideas-ref' }, h('summary', {}, '它能看到什么、能做什么'),
    h('div', { class: 'small' }, h('b', {}, '它能看到（“如果”后面说这些）'), h('ul', {}, ...sees.map((t) => h('li', {}, t))),
      h('b', {}, '它会做（“就”后面说这些）'), h('ul', {}, ...[...Object.values(MOVE_ACTIONS), ...Object.values(FIRE_ACTIONS)].map((t) => h('li', {}, t))))));
  return box;
}
